import { KARLA_FONT_FAMILY } from 'libs/end-to-end/src/types/branding/fonts';
import { z } from 'zod';

// Line Chart Configuration Types

export const LineStyle = z.enum(['solid', 'dashed', 'dotted', 'pattern']);
export type LineStyle = z.infer<typeof LineStyle>;

export const CurveType = z.enum([
  'linear',
  'monotoneX',
  'monotoneY',
  'cardinal',
  'step',
]);
export type CurveType = z.infer<typeof CurveType>;

export const LegendPosition = z.enum([
  'top',
  'bottom',
  'left',
  'right',
  'inline',
]);
export type LegendPosition = z.infer<typeof LegendPosition>;

export const LegendStyle = z.enum(['traditional', 'buttons', 'compact']);
export type LegendStyle = z.infer<typeof LegendStyle>;

export const PatternDefinition = z.object({
  id: z.string(),
  type: z.enum(['patternLines', 'patternDots', 'patternSquares']),
  background: z.string().optional(),
  color: z.string().optional(),
  rotation: z.number().optional(),
  lineWidth: z.number().optional(),
  spacing: z.number().optional(),
});
export type PatternDefinition = z.infer<typeof PatternDefinition>;

export const PointStyle = z.object({
  enabled: z.boolean(),
  size: z.number().optional(),
  color: z.string().optional(),
  borderWidth: z.number().optional(),
  borderColor: z.string().optional(),
});
export type PointStyle = z.infer<typeof PointStyle>;

export const SeriesStyleConfig = z.object({
  color: z.string().optional(),
  width: z.number().optional(),
  style: LineStyle.optional(),
  pattern: PatternDefinition.optional(),
  curve: CurveType.optional(),
  opacity: z.number().optional(),
  points: PointStyle.optional(),
});
export type SeriesStyleConfig = z.infer<typeof SeriesStyleConfig>;

export const MarkerConfig = z.object({
  id: z.string(),
  axis: z.enum(['x', 'y']),
  value: z.union([z.string(), z.number(), z.coerce.date()]),
  color: z.string().optional(),
  strokeWidth: z.number().optional(),
  strokeOpacity: z.number().optional(),
  label: z.string().optional(),
  labelOrientation: z.enum(['horizontal', 'vertical']).optional(),
  labelPosition: z
    .enum([
      'top-left',
      'top',
      'top-right',
      'right',
      'bottom-right',
      'bottom',
      'bottom-left',
      'left',
    ])
    .optional(),
  arrow: z
    .object({
      enabled: z.boolean(),
      fromSeries: z.string(),
      toSeries: z.string(),
      headSize: z.number().optional(),
    })
    .optional(),
  gradientArea: z
    .object({
      enabled: z.boolean(),
      startX: z.union([z.string(), z.number()]).optional(),
      endX: z.union([z.string(), z.number()]).optional(),
      opacity: z.number().optional(),
    })
    .optional(),
});
export type MarkerConfig = z.infer<typeof MarkerConfig>;

export const LegendConfig = z.object({
  enabled: z.boolean(),
  position: LegendPosition,
  justify: z.boolean().optional(),
  style: LegendStyle,
  orientation: z.enum(['horizontal', 'vertical']).optional(),
});
export type LegendConfig = z.infer<typeof LegendConfig>;

export const LineChartConfig = z.object({
  series: z.record(z.string(), SeriesStyleConfig),
  defaultLineStyle: SeriesStyleConfig,
  legend: LegendConfig,
  markers: z.array(MarkerConfig),
  showTreatmentGradient: z.boolean(),
  theme: z
    .object({
      colors: z.array(z.string()).optional(),
      fontFamily: z.string().optional(),
      fontSize: z.number().optional(),
      gridColor: z.string().optional(),
      axisColor: z.string().optional(),
    })
    .optional(),
});
export type LineChartConfig = z.infer<typeof LineChartConfig>;

export const createDefaultLineChartConfig = (): LineChartConfig => ({
  series: {},
  defaultLineStyle: {
    width: 2,
    style: 'solid',
    opacity: 1,
    curve: 'linear',
    points: {
      enabled: true,
      size: 6,
      borderWidth: 2,
    },
  },
  legend: {
    enabled: true,
    position: 'bottom',
    style: 'buttons',
    orientation: 'horizontal',
  },
  markers: [],
  showTreatmentGradient: false,
  theme: {
    fontFamily: KARLA_FONT_FAMILY,
    fontSize: 12,
    gridColor: '#E5E7EB',
    axisColor: '#696D70',
  },
});
